// Section 18: Guard mandate policy. Deter only / Patrol stores (default) / Escort foragers.
// Escort: forager risk 0.5x and theft deterrence 0.8x. Deter only: violent-crime deterrence 1.2x,
// and theft detection gets no per-guard bonus (the guards do not patrol the stores; E1-06).
// Patrol stores is the baseline (theft detection +10% per guard, Section 13). Per-type deterrence
// scales the crime draw's weights, so it enters a person's crime chance as one factor.
// Constants: config.policies.defaults.guardMandate, config.policies.escort*, deterOnly*.
import type { Config } from '../../config';
import { mod } from '../../core/modifiers';
import { noChangeCost, noProblem } from '../../core/content-helpers';
import { guardsOnDuty, mandateCrimeFactor } from '../../core/queries';
import { definePolicy } from '../../core/registry';
import type { Modifier } from '../../core/types';

export function guardMandate(config: Config) {
  return definePolicy({
    id: 'guardMandate',
    label: 'Guard mandate',
    section: 'S18',
    group: 'justice',
    options: ['deter', 'patrol-stores', 'escort-foragers'],
    default: config.policies.defaults.guardMandate,
    modifierTargets: ['jobRisk', 'crimeRisk'],
    modifiers(ctx, target): readonly Modifier[] {
      const mandate = ctx.state.policies.guardMandate;
      if (target.kind === 'jobRisk') {
        if (target.jobId !== 'forager' || mandate !== 'escort-foragers') return [];
        return [mod.mul('policy:guardMandate', ctx.cfg.policies.escortForagerRisk, 'escorted')];
      }
      if (target.kind !== 'crimeRisk' || mandate === 'patrol-stores') return [];
      const p = ctx.state.people[target.personId];
      if (p === undefined) return [];
      const factor = mandateCrimeFactor(ctx.state, ctx.cfg, p);
      if (factor === 1) return [];
      const note =
        mandate === 'escort-foragers' ? 'guards escort foragers' : 'guards deter violence';
      return [mod.mul('policy:guardMandate', factor, note)];
    },
    changeCost: noChangeCost,
    // M6: what the guards on duty do under each mandate (the modifiers above and Section 13).
    affects(ctx, option) {
      const guards = guardsOnDuty(ctx.state, ctx.cfg).length;
      const c = ctx.cfg.policies;
      return [
        {
          key: `policy.guardMandate.${option}`,
          params: {
            guards,
            detection: Math.round(ctx.cfg.crime.detectionPerGuard * 100),
            violent: c.deterOnlyViolentDeterrence,
            risk: c.escortForagerRisk,
            theft: c.escortTheftDeterrence,
          },
        },
      ];
    },
    validate: noProblem,
  });
}
